import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { TeamMember, PendingInvitation, Role } from "../types";
import { toast } from "sonner";
import { useAuditLog } from "@/hooks/use-audit-log";
import { formatRoleName } from "../utils";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { LoginResponse } from "@/features/auth/types";
import { AcceptInvitationFormData } from "@/lib/validators/auth";
import { AxiosError, isAxiosError } from "axios";
import {
  PaginatedInvitationsResponse,
  paginatedInvitationsResponseSchema,
  paginatedPermissionsResponseSchema,
  PaginatedTeamMembersResponse,
  paginatedTeamMembersResponseSchema,
  Permission,
  roleSchema,
} from "../schemas/team";
import z from "zod";
import { pendingApprovalResponseSchema } from "@/features/approvals/schemas/approval.schema";

interface ApiTeamMember {
  id: number;
  name: string;
  email: string;
  roleId: number;
  status: "active" | "inactive" | "pending";
  createdAt: string;
  lastLogin?: string;
}

interface TeamMembersResponse {
  data: {
    team: {
      data: ApiTeamMember[];
    };
  };
}

interface PendingInvitationsResponse {
  data: {
    invitations: PendingInvitation[];
  };
}

export interface TeamFilters {
  page: number;
  perPage: number;
  search?: string;
}

// export const useTeamMembers = () => {
//   return useQuery<TeamMember[], Error>({
//     queryKey: ["teamMembers"],
//     queryFn: async () => {
//       const { data } = await apiClient.get<TeamMembersResponse>(
//         "/v1/admin/team"
//       );

//       const uniqueRoleIds = [
//         ...new Set(data.data.team.data.map((member) => member.roleId)),
//       ];

//       const rolePromises = uniqueRoleIds.map((roleId) =>
//         apiClient.get<Role>(`/v1/admin/roles/${roleId}`)
//       );

//       const roleResponses = await Promise.all(rolePromises);
//       const rolesMap = new Map<number, Role>();

//       roleResponses.forEach((response) => {
//         rolesMap.set(response.data.id, response.data);
//       });

//       const teamMembers = data.data.team.data.map(
//         (member): TeamMember => ({
//           id: member.id,
//           name: member.name,
//           email: member.email,
//           roleId: member.roleId,
//           role: rolesMap.get(member.roleId),
//           status: member.status,
//           createdAt: member.createdAt,
//           lastLogin: member.lastLogin || "",
//         })
//       );

//       console.log("Team members (hook): ", teamMembers);
//       return teamMembers;
//     },
//   });
// };

export const useTeamMembers = (filters: TeamFilters) => {
  return useQuery<PaginatedTeamMembersResponse, Error>({
    queryKey: ["teamMembers", filters],
    queryFn: async () => {
      const response = await apiClient.get("/v1/admin/team", {
        params: filters,
      });

      const paginatedTeam = response.data.data.info.team;
      return paginatedTeam;
    },
    placeholderData: keepPreviousData,
  });
};

export const usePendingInvitations = (filters: TeamFilters) => {
  return useQuery<PaginatedInvitationsResponse, Error>({
    queryKey: ["pendingInvitations", filters],
    queryFn: async () => {
      const response = await apiClient.get("/v1/admin/invitations", {
        params: filters,
      });

      const paginatedInvitations = response.data.data.info.invitations;

      return paginatedInvitations;
    },
    placeholderData: keepPreviousData,
  });
};

export const useRoles = () => {
  return useQuery<Role[], Error>({
    queryKey: ["roles"],
    queryFn: async () => {
      const response = await apiClient.get("/v1/admin/roles");
      // return z.array(roleSchema).parse(response.data.data.info.data);
      return response.data.data.info.data;
    },
  });
};

export const useRole = (roleId: number | undefined) => {
  return useQuery<Role, Error>({
    queryKey: ["role", roleId],
    queryFn: async () => {
      if (!roleId) throw new Error("Role ID is required");
      const { data } = await apiClient.get(`/v1/admin/roles/${roleId}`);
      return data.data.info;
    },
    enabled: !!roleId,
  });
};

/**
 * Fetches the complete, searchable list of all system permissions.
 * Achieved by requesting a large number of items.
 */
export const useAllPermissions = (search?: string) => {
  return useQuery<Permission[], Error>({
    queryKey: ["allPermissions", search],
    queryFn: async () => {
      const response = await apiClient.get(
        "/v1/admin/admin-management/permissions",
        {
          params: {
            per_page: 500,
            search: search || undefined,
          },
        }
      );
      const parsed = paginatedPermissionsResponseSchema.parse(
        response.data.data.info
      );
      return parsed.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

// --- Role Mutations ---

export interface CreateRolePayload {
  name: string;
  description: string;
  permissions: string[];
}

export interface UpdateRolePayload extends CreateRolePayload {
  updatedAt: string; // For optimistic locking
}

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  return useMutation<Role, AxiosError, CreateRolePayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.post(
        "/v1/admin/admin-management/roles",
        payload
      );
      // return roleSchema.parse(response.data.data.info.role);
      return response.data.data.info.role;
    },
    onSuccess: (newRole) => {
      toast.success("Role Created", {
        description: `The role "${newRole.name}" has been successfully created.`,
      });
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error) => {
      console.error("Error creating role: ", error);
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        "An unexpected error occurred.";
      toast.error("Failed to Create Role", { description: errorMessage });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  return useMutation<
    Role,
    AxiosError,
    { roleId: number; payload: UpdateRolePayload }
  >({
    mutationFn: async ({ roleId, payload }) => {
      const response = await apiClient.put(
        `/v1/admin/admin-management/roles/${roleId}`,
        payload
      );
      // return roleSchema.parse(response.data.data.info.role);
      return response.data.data.info.role;
    },
    onSuccess: (updatedRole) => {
      toast.success("Role Updated", {
        description: `The role "${updatedRole.name}" has been saved.`,
      });
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error, variables) => {
      if (error.response?.status === 409) {
        toast.error("Save Conflict", {
          description: `The role "${variables.payload.name}" was modified by another user. Please refresh and try again.`,
          duration: 8000,
        });
      } else {
        console.error("Error updating role: ", error);
        const errorMessage =
          (error.response?.data as { message?: string })?.message ||
          "An unexpected error occurred.";
        toast.error("Update Failed", { description: errorMessage });
      }
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
};

export interface UpdateTeamMemberPayload {
  name: string;
  roleId: number;
  status: "active" | "inactive";
}

const invitePayloadSchema = z.object({
  name: z.string(),
  email: z.string().email(),
  roleId: z.number(),
  customMessage: z.string().optional(),
});
export type InvitePayload = z.infer<typeof invitePayloadSchema>;

export const useInviteTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation<any, AxiosError, InvitePayload>({
    mutationFn: async (payload) => {
      const { data } = await apiClient.post("/v1/admin/invitations", payload);
      return data;
    },
    onSuccess: (response, variables) => {
      const parsedResponse = pendingApprovalResponseSchema.safeParse(
        response.data?.info?.info
      );
      if (parsedResponse.success && parsedResponse.data.approvalPending) {
        toast.info("Invitation Submitted for Approval", {
          description:
            "This invitation requires review by another administrator before it is sent.",
        });
        queryClient.invalidateQueries({ queryKey: ["approvalQueue"] });
      } else {
        toast.success("Invitation Sent!", {
          description: `An invitation has been sent to ${variables.email}.`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["pendingInvitations"] });
    },
    onError: (error) => {
      const errorMessage =
        (error.response?.data as { data?: { error?: string } })?.data?.error ||
        error.message;
      toast.error("Invitation Failed", {
        description: errorMessage || "An unexpected error occurred.",
      });
    },
  });
};

export const useResendInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { invitationId: number }>({
    mutationFn: async ({ invitationId }) => {
      await apiClient.post(`/v1/admin/invitations/${invitationId}/resend`);
    },
    onSuccess: () => {
      toast.success("Invitation Resent", {
        description: "The invitation email has been sent again.",
      });
      queryClient.invalidateQueries({ queryKey: ["pendingInvitations"] });
    },
    onError: (error) => {
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message;
      toast.error("Failed to Resend", { description: errorMessage });
    },
  });
};

export const useCancelInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { invitationId: number }>({
    mutationFn: async ({ invitationId }) => {
      await apiClient.post(`/v1/admin/invitations/${invitationId}/cancel`);
    },
    onSuccess: (_, { invitationId }) => {
      toast.success("Invitation Cancelled", {
        description: "The pending invitation has been successfully cancelled.",
      });
      queryClient.invalidateQueries({ queryKey: ["pendingInvitations"] });
    },
    onError: (error, { invitationId }) => {
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message;
      toast.error("Failed to Cancel", { description: errorMessage });
    },
  });
};

export const useValidateInvitationToken = (token: string | null) => {
  return useQuery({
    queryKey: ["invitation", token],
    queryFn: async () => {
      const { data } = await apiClient.get(`/v1/admin/invitations/${token}`);
      return data.data.info;
    },
    enabled: !!token,
    retry: (failureCount, error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      // Don't retry client errors (401/403/404/...) — they won't succeed.
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });
};

export const useAcceptInvitation = () => {
  const navigate = useNavigate();
  const loginToStore = useAuthStore((state) => state.login);

  return useMutation<
    LoginResponse,
    AxiosError,
    { token: string; payload: AcceptInvitationFormData; user: string }
  >({
    mutationFn: async ({ token, payload }) => {
      const { data } = await apiClient.post(
        `/v1/admin/invitations/${token}/accept`,
        payload
      );
      return data;
    },
    onSuccess: (data, { user }) => {
      // loginToStore(data.admin);

      toast.success("Account Created Successfully!", {
        description: `Welcome, ${user}! Please log in to begin.`,
      });
      navigate("/");
    },
    onError: (error) => {
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message;
      toast.error("Failed to Create Account", {
        description:
          errorMessage || "This invitation link may be invalid or expired.",
      });
    },
  });
};

export const useDeleteInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { invitationId: number }>({
    mutationFn: async ({ invitationId }) => {
      await apiClient.delete(`/v1/admin/invitations/${invitationId}`);
    },
    onSuccess: (_, { invitationId }) => {
      toast.success("Invitation Deleted", {
        description: "The pending invitation has been removed.",
      });
      queryClient.invalidateQueries({ queryKey: ["pendingInvitations"] });
    },
    onError: (error, { invitationId }) => {
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message;
      toast.error("Delete Failed", { description: errorMessage });
    },
  });
};

export const useUpdateTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    AxiosError,
    { adminId: string; payload: UpdateTeamMemberPayload }
  >({
    mutationFn: async ({ adminId, payload }) => {
      const { data } = await apiClient.put(
        `/v1/admin/team/${adminId}`,
        payload
      );
      return data;
    },
    onSuccess: (response) => {
      const parsedResponse = pendingApprovalResponseSchema.safeParse(
        response.data?.info?.info
      );
      if (parsedResponse.success && parsedResponse.data.approvalPending) {
        toast.info("Team Member Update Submitted for Approval", {
          description:
            "This request requires review by another administrator before it takes effect.",
        });
        queryClient.invalidateQueries({ queryKey: ["approvalQueue"] });
      } else {
        toast.success("Team Member Updated", {
          description: "The team member's details have been saved.",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["teamMembers"] });
    },
    onError: (error, variables) => {
      if (error.response?.status === 409) {
        toast.error("Save Conflict", {
          description: `The details for ${variables?.payload?.name} have been changed by another admin. Your update has been cancelled to prevent a conflict. The team list will now refresh.`,
          duration: 10000,
          action: {
            label: "Refresh Now",
            onClick: () =>
              queryClient.invalidateQueries({ queryKey: ["teamMembers"] }),
          },
        });
        // Invalidate the query to force a refetch of the stale data
        queryClient.invalidateQueries({ queryKey: ["teamMembers"] });
      } else {
        const errorMessage =
          (error.response?.data as { message?: string })?.message ||
          error.message;
        toast.error("Update Failed", { description: errorMessage });
      }
    },
  });
};

export const useDeleteTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation<any, AxiosError, { adminId: string }>({
    mutationFn: async ({ adminId }) => {
      const { data } = await apiClient.delete(`/v1/admin/team/${adminId}`);
      return data;
    },
    onSuccess: (response) => {
      const parsedResponse = pendingApprovalResponseSchema.safeParse(
        response.data?.info?.info
      );
      if (parsedResponse.success && parsedResponse.data.approvalPending) {
        toast.info("Delete Request Submitted for Approval", {
          description:
            "This request requires review by another administrator before it takes effect.",
        });
        queryClient.invalidateQueries({ queryKey: ["approvalQueue"] });
      } else {
        toast.success("Team Member Deleted", {
          description: `The team member has been removed.`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["teamMembers"] });
    },
    onError: (error, variables) => {
      if (error.response?.status === 409) {
        toast.error("Delete Conflict", {
          description: `This team member's profile was modified just before your delete action. The deletion has been cancelled for safety. Please review the team list and try again.`,
          duration: 10000,
          action: {
            label: "Refresh Now",
            onClick: () =>
              queryClient.invalidateQueries({ queryKey: ["teamMembers"] }),
          },
        });
        // Invalidate the query to force a refetch of the stale data
        queryClient.invalidateQueries({ queryKey: ["teamMembers"] });
      } else {
        const errorMessage =
          (error.response?.data as { message?: string })?.message ||
          error.message;
        toast.error("Delete Failed", { description: errorMessage });
      }
    },
  });
};
